import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Spinner, Text, VStack, useToast } from '@chakra-ui/react';
import { HTTP } from 'utils/http';
import * as Sentry from '@sentry/react';
import { sentryTrace } from 'utils/sentryTrace';
import { ContractPaymentOverlay, ContractPaymentStep } from 'components/ContractPaymentOverlay';
import { resolveUserError } from 'utils/userErrorMessages';
import { trackPurchase, purchaseItem, purchaseCustomer } from 'utils/tracking';
import {
  getScaPayment,
  getScaPaymentKind,
  clearScaPayment,
  getScaDiagnostics,
  getScaEnvironment,
} from 'utils/scaStorage';

export function ScaCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [isProcessing, setIsProcessing] = useState(true);
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  // Quand le paiement en cours est un contract, on monte l'overlay UX
  // moderne plutôt que l'écran simple ci-dessous.
  const [contractStep, setContractStep] = useState<ContractPaymentStep | null>(() => {
    return getScaPaymentKind() === 'contract' ? 'finalizing' : null;
  });
  const [contractError, setContractError] = useState<string | null>(null);

  useEffect(() => {
    // Suit l'étape atteinte pour tagger précisément un éventuel échec
    // (init → params → restore → validate → 3ds-check → complete-{service,contract} → success).
    let stage = 'init';

    const processScaAuthentication = async () => {
      // Tag global sur tous les events émis pendant ce retour 3DS.
      Sentry.setTag('flow', 'sca');
      // Diagnostic d'environnement : posé en tags/context GLOBAUX pour que
      // tous les events du retour (completed, missing, exception) le portent.
      // `device_id` absent = retour dans un AUTRE contexte navigateur que
      // celui qui a initié le 3DS (à croiser avec le tag device_id de
      // l'event « SCA: 3DS redirect initiated »).
      const scaEnv = getScaEnvironment();
      Sentry.setTag('device_id', scaEnv.deviceId ?? 'none');
      Sentry.setTag('in_app_browser', String(scaEnv.inAppBrowser));
      Sentry.setTag('navigation_type', scaEnv.navigationType ?? 'unknown');
      Sentry.setContext('sca_environment', scaEnv);
      Sentry.addBreadcrumb({ category: 'sca', level: 'info', message: 'SCA callback mounted' });
      try {
        // ÉTAPE 1: Récupérer les paramètres de retour Stripe/Mindbody
        // Les paramètres peuvent être dans la query string (avant #) ou après le hash
        stage = 'params';
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = searchParams;

        // Priorité aux paramètres de query string puis searchParams
        const paymentIntent = urlParams.get('payment_intent') || hashParams.get('payment_intent') || null;
        const paymentIntentClientSecret =
          urlParams.get('payment_intent_client_secret') || hashParams.get('payment_intent_client_secret') || null;
        const redirectStatus = urlParams.get('redirect_status') || hashParams.get('redirect_status') || null;
        // attempt_id propagé par CheckoutForm dans la ScaReturnUrl : corrèle
        // ce retour avec son event « SCA: 3DS redirect initiated », même si
        // le storage est vide.
        const scaAttempt = urlParams.get('attempt') || hashParams.get('attempt') || null;
        Sentry.setTag('sca_attempt', scaAttempt ?? 'none');

        // ÉTAPE 2: Discriminer service vs contract via la clé posée avant 3DS
        stage = 'restore';
        const paymentKind = getScaPaymentKind();
        Sentry.setTag('payment_kind', paymentKind);
        Sentry.setTag('redirect_status', redirectStatus ?? 'none');
        const sessionKey = paymentKind === 'contract' ? 'scaContractPaymentData' : 'scaPaymentData';

        const { raw: savedPaymentData, source: storageSource } = getScaPayment(sessionKey);
        Sentry.setTag('storage_source', storageSource ?? 'none');
        if (!savedPaymentData) {
          // C'est précisément le #6978 que ce fix localStorage cible. On capture
          // un event dédié avec diagnostic du stockage AVANT de throw, pour
          // distinguer la cause : storage indisponible vs réellement vide
          // (retour cross-onglet/webview non rattrapé) vs payload périmé.
          const diagnostics = getScaDiagnostics();
          Sentry.captureMessage('SCA: payment data missing at 3DS return', {
            level: 'error',
            tags: { flow: 'sca', sca_stage: 'return', payment_kind: paymentKind },
            extra: {
              ...diagnostics,
              sessionKey,
              hasPaymentIntent: !!paymentIntent,
              hasClientSecret: !!paymentIntentClientSecret,
              redirectStatus,
            },
          });
          // L'event ci-dessus porte déjà le diagnostic : le catch ne doit pas
          // le doubler d'un captureException (51 doublons en deux jours).
          const missing = new Error('Aucune donnée de paiement trouvée en session') as Error & {
            sentryReported?: boolean;
          };
          missing.sentryReported = true;
          throw missing;
        }
        Sentry.addBreadcrumb({
          category: 'sca',
          level: 'info',
          message: 'SCA payment data restored from storage',
          data: { paymentKind, storageSource },
        });

        const paymentData = JSON.parse(savedPaymentData);

        // ÉTAPE 3: Vérifications
        stage = 'validate';
        if (paymentKind === 'service' && !paymentData.ServiceId) {
          throw new Error('ServiceId manquant dans les données de session');
        }
        if (paymentKind === 'contract' && !paymentData.ContractId) {
          throw new Error('ContractId manquant dans les données de session');
        }

        // TransactionIDs requis uniquement pour les services (checkoutshoppingcart) ;
        // purchasecontract ne les expose pas, on re-poste tel quel.
        if (
          paymentKind === 'service' &&
          (!paymentData.TransactionIDs ||
            !Array.isArray(paymentData.TransactionIDs) ||
            paymentData.TransactionIDs.length === 0)
        ) {
          throw new Error('TransactionIDs manquants ou invalides dans les données de session');
        }

        // ÉTAPE 4: Vérifier le statut de l'authentification
        // Pour Stripe, on vérifie la présence des paramètres de succès
        stage = '3ds-check';
        // Apple Pay (paiement alternatif Mindbody) : la page hébergée par
        // Mindbody Payments ne renvoie pas forcément les paramètres Stripe
        // payment_intent. On laisse alors le second appel (TransactionIDs)
        // trancher : Mindbody refuse une transaction non autorisée.
        const isApplePayReturn = paymentKind === 'service' && paymentData.PaymentMethod === 'Apple Pay';
        const authenticationSuccessful = isApplePayReturn
          ? redirectStatus !== 'failed'
          : !!(paymentIntent && paymentIntentClientSecret);


        if (!authenticationSuccessful) {
          // Si l'authentification a échoué ou est incomplète

          throw new Error(`Authentification 3D Secure échouée ou incomplète. Status: ${redirectStatus || 'unknown'}`);
        }

        setStatus('processing');

        // ÉTAPE 5: Préparer les données pour le second appel selon le kind
        if (paymentKind === 'contract') {
          stage = 'complete-contract';
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { ContractId: _cid, originalResponse: _orig, ...paymentOnlyData } = paymentData;
          const completionData = {
            ContractId: paymentData.ContractId,
            LocationId: mondayMindbody?.pricingConfig?.defaultLocationId ?? 1,
            Payment: paymentOnlyData,
            Test: false,
          };
          const contractResponse = (await HTTP.post('/sale/scacomplete-contract', completionData).send()) as {
            ShoppingCart?: { Id?: string | number };
            ClientContractId?: number | null;
          };

          // GA4 purchase : abonnement activé après authentification 3DS.
          // scacomplete-contract re-poste purchasecontract : pas de ShoppingCart
          // dans la réponse, le transaction_id est le ClientContractId.
          trackPurchase({
            transactionId: contractResponse?.ShoppingCart?.Id ?? contractResponse?.ClientContractId ?? undefined,
            value: Number(paymentData.TotalAmount) || 0,
            items: [purchaseItem(paymentData.ContractId, Number(paymentData.TotalAmount) || 0)],
            paymentType: 'Credit Card',
            coupon: paymentData.PromoCode || undefined,
            customer: purchaseCustomer({ email: paymentData.Email, phone: paymentData.Phone }),
          });

          // Succès contract : breadcrumb (un event par paiement réussi brûlait
          // le quota, 311 en deux jours toutes natures confondues).
          stage = 'success';
          sentryTrace('SCA: purchase completed after 3DS', {
            tags: { flow: 'sca', sca_stage: 'complete', payment_kind: 'contract' },
            extra: { cartId: contractResponse?.ShoppingCart?.Id ?? null },
          });

          clearScaPayment();
          setStatus('success');
          setContractStep('success');

          toast({
            status: 'success',
            title: 'Abonnement activé !',
            description: 'Votre abonnement peut apparaître dans Mon Compte d\'ici 1 minute.',
            duration: 5000,
            isClosable: true,
          });

          setTimeout(() => {
            navigate('/account#mon-abonnement', { replace: true });
          }, 2500);
          return;
        }

        // Service : comportement existant
        stage = 'complete-service';
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { ServiceId: _, TransactionIDs: __, originalResponse: ___, ...paymentOnlyData } = paymentData;

        const completionData = {
          ServiceId: paymentData.ServiceId,
          TransactionIDs: paymentData.TransactionIDs,
          Payment: {
            // Inclure seulement les données de paiement, pas les métadonnées de transaction
            ...paymentOnlyData,
          },
        };

        // ÉTAPE 6: Effectuer le second appel API
        const serviceResponse = (await HTTP.post('/sale/scacomplete', completionData).send()) as {
          ShoppingCart?: { Id?: string | number };
        };

        // GA4 purchase : achat de service abouti après authentification 3DS.
        trackPurchase({
          transactionId: serviceResponse?.ShoppingCart?.Id,
          value: Number(paymentData.TotalAmount) || 0,
          items: [purchaseItem(paymentData.ServiceId, Number(paymentData.TotalAmount) || 0)],
          paymentType: paymentData.PaymentMethod || 'Credit Card',
          coupon: paymentData.PromoCode || undefined,
          customer: purchaseCustomer({ email: paymentData.Email, phone: paymentData.Phone }),
        });

        // Succès service : breadcrumb, même raison que pour le contract.
        stage = 'success';
        sentryTrace('SCA: purchase completed after 3DS', {
          tags: { flow: 'sca', sca_stage: 'complete', payment_kind: 'service' },
          extra: { cartId: serviceResponse?.ShoppingCart?.Id ?? null },
        });

        // ÉTAPE 7: Nettoyer la session et rediriger
        clearScaPayment();
        setStatus('success');

        toast({
          status: 'success',
          title: 'Paiement réussi !',
          description: 'Votre achat a été confirmé avec succès.',
          duration: 5000,
          isClosable: true,
        });

        // Redirection après succès
        setTimeout(() => {
          navigate('/account/services', { replace: true });
        }, 2000);
      } catch (error) {

        const { title, description } = resolveUserError('sca', error);
        const overlayMessage = [title, description].filter(Boolean).join('\n\n');

        // Tag par l'étape atteinte → on sait immédiatement, dans Sentry, si
        // l'échec vient du stockage (restore), de la validation, du challenge
        // 3DS (3ds-check) ou du 2e appel API (complete-*).
        if (!(error as { sentryReported?: boolean } | null)?.sentryReported) {
          Sentry.captureException(error, {
            tags: { flow: 'sca', sca_stage: stage, payment_kind: getScaPaymentKind() },
          });
        }
        setStatus('error');
        if (contractStep) {
          setContractStep('failed');
          setContractError(overlayMessage);
        }

        toast({
          status: 'error',
          title,
          description,
          duration: 8000,
          isClosable: true,
        });

        // En cas d'erreur, rediriger vers la page de checkout après un délai
        setTimeout(() => {
          clearScaPayment();
          navigate('/', { replace: true });
        }, 5000);
      } finally {
        setIsProcessing(false);
      }
    };

    // Démarrer le processus après un court délai pour laisser la page se charger
    const timeoutId = setTimeout(processScaAuthentication, 500);
    return () => clearTimeout(timeoutId);
  }, [searchParams, navigate, toast]);

  const getMessage = () => {
    switch (status) {
      case 'processing':
        return 'Finalisation de votre authentification 3D Secure...';
      case 'success':
        return 'Authentification réussie ! Redirection en cours...';
      case 'error':
        return "Échec de l'authentification. Redirection vers le checkout...";
      default:
        return '';
    }
  };

  const getColor = () => {
    switch (status) {
      case 'processing':
        return 'blue.500';
      case 'success':
        return 'green.500';
      case 'error':
        return 'red.500';
      default:
        return 'gray.500';
    }
  };

  // En mode contract, on monte le ContractPaymentOverlay (UI moderne) à la
  // place de l'écran simple. Pour le mode service, on garde le rendu legacy.
  if (contractStep) {
    return <ContractPaymentOverlay isOpen currentStep={contractStep} errorMessage={contractError} />;
  }

  return (
    <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="gray.50">
      <VStack spacing={6} textAlign="center" p={8} bg="white" borderRadius="lg" boxShadow="lg">
        {isProcessing ? (
          <Spinner size="xl" color="blue.500" thickness="4px" />
        ) : status === 'success' ? (
          <Box fontSize="4xl" color="green.500">
            ✓
          </Box>
        ) : (
          <Box fontSize="4xl" color="red.500">
            ✗
          </Box>
        )}

        <Text fontSize="xl" fontWeight="semibold" color={getColor()}>
          {getMessage()}
        </Text>

        {status === 'processing' && (
          <Text fontSize="sm" color="gray.600">
            Merci de ne pas fermer cette page pendant le traitement.
          </Text>
        )}

        {status === 'error' && (
          <Text fontSize="sm" color="gray.600">
            Si le problème persiste, veuillez contacter notre support client.
          </Text>
        )}
      </VStack>
    </Box>
  );
}
